import { useState } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ArticleDetail } from '../apis/graphQl/elevio/grappa/types/grappa.types';
import { feedbackActions } from '../elevio.actions/feedback';
import { elevioSelectors } from '../elevio.reducer/selectors';
import { ConversationThread, ParsedMessage } from '../types/messages';
import { ElevioError, getUrl } from '../utils';
import {
  convertMessagesToParsedMessages,
  getTruncatedMessage,
} from '../utils/messages';

import { useArticle } from '.';

export type TextInputElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLFormElement;

/** Feedback form */
export type FeedbackForm = {
  email: string;
  feedback: string;
};

const getOnChangeValue = (
  eventOrValue: React.SyntheticEvent<TextInputElement> | string,
) => {
  const value: string =
    typeof eventOrValue === 'string'
      ? (eventOrValue as string)
      : eventOrValue.currentTarget.value;
  return value;
};

function getTicketThread(
  ticketMessages: readonly ParsedMessage[],
): ConversationThread {
  return ticketMessages.reduce<ConversationThread>((acc, ticketMessage) => {
    const { message, timestamp, sender_type } = ticketMessage;
    if (!message) return acc;
    return acc.concat({
      message: getTruncatedMessage(message),
      timestamp,
      sender_type,
    });
  }, []);
}

/**
 * # useArticleFeedback
 *
 * A hook for Elevio article feedback data and actions
 */
export function useArticleFeedback() {
  const { feedback, stage } = useAppSelector(elevioSelectors.articleFeedback);
  const suggestedArticleIds = useAppSelector(
    elevioSelectors.suggestion.suggestedArticleIds,
  );
  const userEmail = useAppSelector(elevioSelectors.user.email);

  const dispatch = useAppDispatch();

  const pageUrl = getUrl();
  const articleId = useAppSelector((store) => elevioSelectors.articleId(store));
  const tagNames = useAppSelector(elevioSelectors.conversation.tagNames);
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const conversationSubject = useAppSelector(
    elevioSelectors.conversation.subject,
  );
  const messages = useAppSelector(elevioSelectors.conversation.messages);
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  if (!articleId || !userEmail || !conversationId) {
    throw ElevioError({
      context: 'useArticleFeedback',
      feature: 'feedback',
      message: `no ${
        !articleId ? 'articleId' : !userEmail ? 'userEmail' : 'conversationId'
      } found`,
    });
  }

  const { articleData } = useArticle({
    id: articleId,
    languageId,
    pageUrl,
  });

  const [hasSubmitted, setHasSubmitted] = useState(false);

  if (!articleData || !articleData.article) {
    throw ElevioError({
      context: 'useArticleFeedback',
      feature: 'feedback',
      message: 'no articleData found',
    });
  }

  const article = articleData.article as ArticleDetail;
  const url = window.location.href;

  const handlePositive = () => {
    const parsedMessages: ParsedMessage[] =
      convertMessagesToParsedMessages(messages);
    const ticketThread = getTicketThread(parsedMessages);

    dispatch(
      feedbackActions.ticketAssisted({
        assistType: 'helpful_click',
        ticketId: conversationId.toString(),
        ticketThread,
        ticketSubject: conversationSubject,
        articleId,
        articleTags: tagNames,
        suggestedArticleIds,
        url,
      }),
    );
  };

  const handleNegative = () => {
    dispatch(
      feedbackActions.haveFeedbackPressed({
        isPositive: false,
        email: userEmail,
      }),
    );
  };

  const handleFeedbackSkipped = () => {
    dispatch(feedbackActions.feedbackReset());
  };

  const handleFeedbackSubmitted = (formData: FeedbackForm) => {
    dispatch(
      feedbackActions.feedbackSubmit({
        articleId,
        articleLastPublishedAt: new Date(article.lastPublishedAt).toISOString(),
        feedbackText: formData.feedback,
        ticketId: conversationId?.toString(),
        url,
        languageId,
      }),
    );
  };

  const handleMessageChange = (
    eventOrValue: React.ChangeEvent<TextInputElement>,
  ) => {
    const val = getOnChangeValue(eventOrValue);
    dispatch(
      feedbackActions.feedbackChanged({
        feedback: val,
      }),
    );
  };

  const messageError = !feedback ? 'Required' : null;

  const emailError = !userEmail.match(/^.+@.+\..+$/) ? 'Invalid email' : null;

  const onSubmit = (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setHasSubmitted(true);

    // Bail and let the user fix the validation issues.
    if (messageError || emailError) return;

    handleFeedbackSubmitted({ email: userEmail, feedback });
  };

  return {
    stage,
    values: { email: userEmail, feedback },
    hasSubmitted,
    messageError: hasSubmitted ? messageError : null,
    emailError: hasSubmitted ? emailError : null,
    onPositiveReaction: handlePositive,
    onNegativeReaction: handleNegative,
    onFeedbackSkipped: handleFeedbackSkipped,
    handleMessageChange,
    onSubmit,
  };
}
